import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Eye } from "lucide-react";
import { toast } from "sonner";
import AdminTopupRequests, { useTopupRequests } from "./AdminTopupRequests";

const STATUS_BADGE = {
    pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    active: "bg-[#00b67a]/15 text-[#00b67a] border-[#00b67a]/30",
    suspended: "bg-red-500/15 text-red-400 border-red-500/30",
    replaced: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    rejected: "bg-red-500/15 text-red-400 border-red-500/30",
    cancelled: "bg-white/[0.06] text-muted-foreground border-white/10",
};

export default function AdminAdAccounts() {
    const [accounts, setAccounts] = useState([]);
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [form, setForm] = useState({
        user_id: "",
        platform: "Meta",
        name: "",
        external_id: "",
        vertical: "whiteHat",
        balance: 0,
        note: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [tab, setTab] = useState("accounts");
    const topups = useTopupRequests();
    const pendingTopups = topups.items.filter((x) => x.status === "pending").length;

    const load = async () => {
        try {
            const { data } = await api.get("/admin/ad-accounts");
            setAccounts(data);
        } catch {}
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data);
        } catch {}
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        if (!form.user_id || !form.name) {
            toast.error("User and account name required");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/admin/ad-accounts", {
                ...form,
                balance: parseFloat(form.balance) || 0,
            });
            toast.success("Account created");
            setOpen(false);
            setForm({ user_id: "", platform: "Meta", name: "", external_id: "", vertical: "whiteHat", balance: 0, note: "" });
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/ad-accounts/${id}`, { status });
            toast.success("Updated");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        }
    };

    const openDetails = (account) => {
        setSelectedAccount(account);
        setDetailsOpen(true);
    };

    return (
        <div data-testid="admin-ad-accounts" className="space-y-6">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">Ad Accounts</h1>
                    <p className="text-muted-foreground mt-1">
                        Talep edilen reklam hesaplarını yönetin, detayları inceleyin.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="create-ad-account-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                            <Plus className="w-4 h-4 mr-2" /> Create account
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-white/10 max-w-md">
                        <DialogHeader>
                            <DialogTitle>Create ad account for user</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={create} className="space-y-3" data-testid="create-account-form">
                            <div className="space-y-1.5">
                                <Label>User</Label>
                                <select
                                    value={form.user_id}
                                    onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
                                    data-testid="create-user-select"
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm"
                                >
                                    <option value="">— Select user —</option>
                                    {users.filter((u) => u.role === "user").map((u) => (
                                        <option key={u.id} value={u.id}>{u.full_name} · {u.email}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Platform</Label>
                                    <Input
                                        value={form.platform}
                                        onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}
                                        className="bg-background border-white/10"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Vertical</Label>
                                    <select
                                        value={form.vertical}
                                        onChange={(e) => setForm((f) => ({ ...f, vertical: e.target.value }))}
                                        className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm"
                                    >
                                        <option value="whiteHat">White-hat</option>
                                        <option value="grayHat">Gray-hat</option>
                                        <option value="casino">Casino</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Account name (BM ID / display)</Label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="META: ClientBrand-01"
                                    className="bg-background border-white/10"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>External ID</Label>
                                    <Input
                                        value={form.external_id}
                                        onChange={(e) => setForm((f) => ({ ...f, external_id: e.target.value }))}
                                        placeholder="act_xxxxxxx"
                                        className="bg-background border-white/10"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Initial balance ($)</Label>
                                    <Input
                                        type="number"
                                        value={form.balance}
                                        onChange={(e) => setForm((f) => ({ ...f, balance: e.target.value }))}
                                        className="bg-background border-white/10"
                                    />
                                </div>
                            </div>
                            <Button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground" data-testid="create-submit">
                                {submitting ? "Creating…" : "Create"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex items-center gap-1 border-b border-white/10" data-testid="admin-ad-tabs">
                {[
                    { k: "accounts", label: "Hesaplar", n: accounts.length },
                    { k: "topups", label: "Top-up Talepleri", n: pendingTopups, hot: pendingTopups > 0 },
                ].map((tb) => (
                    <button key={tb.k} type="button" onClick={() => setTab(tb.k)} data-testid={`admin-ad-tab-${tb.k}`} data-active={tab === tb.k}
                        className={`relative px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 ${tab === tb.k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        {tb.label}
                        <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-mono ${tb.hot ? "bg-yellow-500/20 text-yellow-300" : "bg-white/[0.06] text-muted-foreground"}`} data-testid={`admin-ad-tab-count-${tb.k}`}>{tb.n}</span>
                    </button>
                ))}
            </div>

            {tab === "topups" ? (
                <AdminTopupRequests items={topups.items} loading={topups.loading} reload={topups.load} />
            ) : (
            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left p-4">Account</th>
                            <th className="text-left p-4 hidden md:table-cell">User</th>
                            <th className="text-left p-4 hidden lg:table-cell">Vertical</th>
                            <th className="text-left p-4">Status</th>
                            <th className="text-right p-4">Balance</th>
                            <th className="text-right p-4">Details</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {accounts.map((a) => {
                            const u = users.find((x) => x.id === a.user_id);
                            return (
                                <tr key={a.id} data-testid={`admin-ad-row-${a.id}`}>
                                    <td className="p-4">
                                        <p className="font-medium">{a.name}</p>
                                        <p className="text-xs font-mono text-muted-foreground">
                                            {a.platform} · {a.external_id || "no ID"}
                                        </p>
                                    </td>
                                    <td className="p-4 hidden md:table-cell text-xs font-mono">
                                        {u?.email || a.user_id.slice(0, 8)}
                                    </td>
                                    <td className="p-4 hidden lg:table-cell text-xs font-mono">
                                        {a.vertical}
                                    </td>
                                    <td className="p-4">
                                        <select
                                            value={a.status}
                                            onChange={(e) => updateStatus(a.id, e.target.value)}
                                            data-testid={`admin-ad-status-${a.id}`}
                                            className={`bg-background border border-white/10 rounded-lg px-2 py-1 text-xs ${STATUS_BADGE[a.status] || ""}`}
                                        >
                                            <option value="pending">pending</option>
                                            <option value="active">active</option>
                                            <option value="suspended">suspended</option>
                                            <option value="replaced">replaced</option>
                                            <option value="rejected">rejected (refund)</option>
                                            <option value="cancelled">cancelled (refund)</option>
                                        </select>
                                    </td>
                                    <td className="p-4 text-right font-mono">
                                        ${(a.balance || 0).toLocaleString()}
                                    </td>
                                    <td className="p-4 text-right">
                                        <Button
                                            variant="outline"
                                            onClick={() => openDetails(a)}
                                            data-testid={`admin-ad-details-${a.id}`}
                                            className="border-white/10"
                                        >
                                            <Eye className="w-4 h-4 mr-2" /> View
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {accounts.length === 0 && (
                    <p className="p-12 text-center text-sm text-muted-foreground">No ad accounts.</p>
                )}
            </div>
            )}

            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="bg-card border-white/10 max-w-3xl">
                    <DialogHeader>
                        <DialogTitle data-testid="ad-request-details-title">Ad request details</DialogTitle>
                    </DialogHeader>
                    {selectedAccount ? (
                        <div className="space-y-4 text-sm" data-testid="ad-request-details-content">
                            <div className="grid md:grid-cols-3 gap-3">
                                <InfoBlock label="Platform" value={selectedAccount.platform} testId="detail-platform" />
                                <InfoBlock label="Status" value={selectedAccount.status} testId="detail-status" />
                                <InfoBlock label="Vertical" value={selectedAccount.vertical} testId="detail-vertical" />
                                <InfoBlock label="Account Type" value={selectedAccount.account_type || "-"} testId="detail-account-type" />
                                <InfoBlock label="Account Name" value={selectedAccount.account_name || selectedAccount.name || "-"} testId="detail-account-name" />
                                <InfoBlock label="Timezone" value={selectedAccount.timezone || "-"} testId="detail-timezone" />
                                <InfoBlock label="BM ID" value={selectedAccount.business_manager_id || "-"} testId="detail-bmid" />
                                <InfoBlock label="Paid from wallet" value={`$${Number(selectedAccount.wallet_debit_applied ?? selectedAccount.initial_topup_usd ?? 0).toFixed(2)}`} testId="detail-initial-topup" />
                                <InfoBlock label="Fee deducted" value={selectedAccount.fee_amount_applied != null ? `$${Number(selectedAccount.fee_amount_applied).toFixed(2)} (${Number(selectedAccount.fee_percent_applied || 0).toFixed(2)}%)` : "-"} testId="detail-fee" />
                                <InfoBlock label="Load to ad account" value={selectedAccount.net_to_account_applied != null ? `$${Number(selectedAccount.net_to_account_applied).toFixed(2)}` : "-"} testId="detail-net" />
                                <InfoBlock label="Refunded" value={selectedAccount.refunded_at ? new Date(selectedAccount.refunded_at).toLocaleString() : "-"} testId="detail-refunded" />
                                <InfoBlock label="Domains change often" value={selectedAccount.domains_change_often ? "Yes" : "No"} testId="detail-domains-change" />
                            </div>

                            <DetailList
                                title="Advertising URLs"
                                items={selectedAccount.advertising_links || []}
                                testId="detail-advertising-links"
                            />

                            <DetailList
                                title="Facebook Pages"
                                items={selectedAccount.facebook_pages || []}
                                testId="detail-facebook-pages"
                            />

                            <DetailList
                                title="Industry Tags"
                                items={selectedAccount.industry_tags || []}
                                testId="detail-industry-tags"
                            />

                            <div>
                                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Note</p>
                                <div className="rounded-xl border border-white/10 bg-background/50 p-3" data-testid="detail-note">
                                    {selectedAccount.note || "-"}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function InfoBlock({ label, value, testId }) {
    return (
        <div className="rounded-xl border border-white/10 bg-background/40 p-3" data-testid={testId}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
            <p className="font-medium mt-1 break-all">{value || "-"}</p>
        </div>
    );
}

function DetailList({ title, items, testId }) {
    const normalized = (items || []).map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
            const name = item.name ? `${item.name}` : "";
            const url = item.url ? `${item.url}` : "";
            return [name, url].filter(Boolean).join(" · ") || "-";
        }
        return String(item || "-");
    });

    return (
        <div data-testid={testId}>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{title}</p>
            {normalized.length ? (
                <ul className="space-y-1.5 rounded-xl border border-white/10 bg-background/50 p-3">
                    {normalized.map((item, idx) => (
                        <li key={`${item}-${idx}`} className="font-mono text-xs break-all text-foreground/85">• {item}</li>
                    ))}
                </ul>
            ) : (
                <div className="rounded-xl border border-white/10 bg-background/50 p-3 text-muted-foreground">-</div>
            )}
        </div>
    );
}
